import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  ChartBarIcon,
  BanknotesIcon,
  ClockIcon,
  UserGroupIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  PrinterIcon,
  EyeIcon,
  ShieldCheckIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

interface ReportData {
  totalPayments: number;
  totalAmount: number;
  averageProcessingTime: number;
  complianceRate: number;
  monthlyTrend: number;
  topDepartments: Array<{
    name: string;
    amount: number;
    employees: number;
  }>;
  monthlyPayments: Array<{
    month: string;
    payments: number;
    amount: number;
  }>;
  paymentsByStatus: Array<{
    status: string;
    count: number;
    percentage: number;
  }>;
}

interface ReportsTabProps {
  reportData: ReportData;
}

const ReportsTab: React.FC<ReportsTabProps> = ({ reportData }) => {
  const [reportType, setReportType] = useState('overview');
  const [selectedPeriod, setSelectedPeriod] = useState('month');

  const reportTypes = [
    { id: 'overview', name: 'نظرة عامة', icon: ChartBarIcon },
    { id: 'compliance', name: 'الامتثال', icon: CheckCircleIcon },
    { id: 'performance', name: 'الأداء', icon: ArrowTrendingUpIcon },
    { id: 'detailed', name: 'تقرير مفصل', icon: DocumentArrowDownIcon }
  ];

  const periods = [
    { id: 'week', name: 'أسبوعي' },
    { id: 'month', name: 'شهري' },
    { id: 'quarter', name: 'ربع سنوي' },
    { id: 'year', name: 'سنوي' }
  ];

  const renderOverviewContent = () => (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl shadow-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-100 text-sm font-medium">إجمالي المدفوعات</p>
              <p className="text-2xl font-bold mt-1">
                {reportData.totalPayments.toLocaleString()}
              </p>
            </div>
            <BanknotesIcon className="h-8 w-8 text-blue-200" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-gradient-to-r from-green-500 to-green-600 rounded-xl shadow-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-green-100 text-sm font-medium">إجمالي المبلغ</p>
              <p className="text-2xl font-bold mt-1">
                {new Intl.NumberFormat('ar-SA', {
                  style: 'currency',
                  currency: 'SAR'
                }).format(reportData.totalAmount)}
              </p>
            </div>
            <BanknotesIcon className="h-8 w-8 text-green-200" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-xl shadow-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-purple-100 text-sm font-medium">وقت المعالجة</p>
              <p className="text-2xl font-bold mt-1">
                {reportData.averageProcessingTime} أيام
              </p>
            </div>
            <ClockIcon className="h-8 w-8 text-purple-200" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl shadow-lg p-6 text-white"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-orange-100 text-sm font-medium">معدل الامتثال</p>
              <p className="text-2xl font-bold mt-1">
                {reportData.complianceRate}%
              </p>
            </div>
            <ShieldCheckIcon className="h-8 w-8 text-orange-200" />
          </div>
        </motion.div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            الاتجاه الشهري للمدفوعات
          </h3>
          <div className="space-y-4">
            {reportData.monthlyPayments.map((data: any, index: number) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {data.month}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {data.payments} مدفوعة
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {new Intl.NumberFormat('ar-SA', {
                      style: 'currency',
                      currency: 'SAR'
                    }).format(data.amount)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
        >
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            أعلى الأقسام من حيث المدفوعات
          </h3>
          <div className="space-y-4">
            {reportData.topDepartments.map((dept, index) => (
              <div key={index} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {dept.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    {dept.employees} موظف
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {new Intl.NumberFormat('ar-SA', {
                      style: 'currency',
                      currency: 'SAR'
                    }).format(dept.amount)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );

  const renderComplianceContent = () => (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          ملخص الامتثال
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <CheckCircleIcon className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-900 dark:text-green-300">
              {reportData.complianceRate}%
            </p>
            <p className="text-sm text-green-600 dark:text-green-400">
              معدل الامتثال العام
            </p>
          </div>
          <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <ClockIcon className="h-8 w-8 text-blue-600 dark:text-blue-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-900 dark:text-blue-300">
              {reportData.averageProcessingTime} أيام
            </p>
            <p className="text-sm text-blue-600 dark:text-blue-400">
              متوسط أيام المعالجة
            </p>
          </div>
          <div className="text-center p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
            <UserGroupIcon className="h-8 w-8 text-purple-600 dark:text-purple-400 mx-auto mb-2" />
            <p className="text-2xl font-bold text-purple-900 dark:text-purple-300">
              100%
            </p>
            <p className="text-sm text-purple-600 dark:text-purple-400">
              تغطية الموظفين
            </p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          تفاصيل الامتثال
        </h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-center">
              <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400 mr-3 rtl:mr-0 rtl:ml-3" />
              <span className="text-gray-900 dark:text-white">المدفوعات في الوقت المحدد</span>
            </div>
            <span className="text-green-600 dark:text-green-400 font-semibold">95.2%</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-center">
              <DocumentTextIcon className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-3 rtl:mr-0 rtl:ml-3" />
              <span className="text-gray-900 dark:text-white">اكتمال الوثائق</span>
            </div>
            <span className="text-blue-600 dark:text-blue-400 font-semibold">98.7%</span>
          </div>
          <div className="flex items-center justify-between p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-3 rtl:mr-0 rtl:ml-3" />
              <span className="text-gray-900 dark:text-white">التحديثات المطلوبة</span>
            </div>
            <span className="text-yellow-600 dark:text-yellow-400 font-semibold">2.3%</span>
          </div>
        </div>
      </motion.div>
    </div>
  );

  const renderPerformanceContent = () => (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          مؤشرات الأداء
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-gradient-to-r from-green-500 to-green-600 rounded-lg text-white">
            <ArrowTrendingUpIcon className="h-8 w-8 mx-auto mb-2" />
            <p className="text-2xl font-bold">+{reportData.monthlyTrend}%</p>
            <p className="text-sm opacity-90">نمو شهري</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg text-white">
            <ClockIcon className="h-8 w-8 mx-auto mb-2" />
            <p className="text-2xl font-bold">{reportData.averageProcessingTime} أيام</p>
            <p className="text-sm opacity-90">متوسط المعالجة</p>
          </div>
          <div className="text-center p-4 bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg text-white">
            <CheckCircleIcon className="h-8 w-8 mx-auto mb-2" />
            <p className="text-2xl font-bold">98.5%</p>
            <p className="text-sm opacity-90">معدل النجاح</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          اتجاهات الأداء
        </h3>
        <div className="space-y-4">
          {reportData.monthlyPayments.map((data: any, index: number) => (
            <div key={index} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{data.month}</p>
                <p className="text-sm text-gray-600 dark:text-gray-400">{data.payments} مدفوعة</p>
              </div>
              <div className="flex items-center">
                <ArrowTrendingUpIcon className="h-4 w-4 text-green-600 dark:text-green-400 mr-2 rtl:mr-0 rtl:ml-2" />
                <span className="text-green-600 dark:text-green-400 font-semibold">
                  +{Math.floor(Math.random() * 10 + 5)}%
                </span>
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  const renderDetailedContent = () => (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700"
      >
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
          التقرير المفصل
        </h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  القسم
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  عدد الموظفين
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  إجمالي المبلغ
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  الحالة
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {reportData.topDepartments.map((dept, index) => (
                <tr key={index}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {dept.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {dept.employees}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-300">
                    {new Intl.NumberFormat('ar-SA', {
                      style: 'currency',
                      currency: 'SAR'
                    }).format(dept.amount)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400">
                      مكتمل
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>
    </div>
  );

  const renderReportContent = () => {
    switch (reportType) {
      case 'overview':
        return renderOverviewContent();
      case 'compliance':
        return renderComplianceContent();
      case 'performance':
        return renderPerformanceContent();
      case 'detailed':
        return renderDetailedContent();
      default:
        return renderOverviewContent();
    }
  };

  return (
    <div className="space-y-6">
      {/* Report Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Report Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              نوع التقرير
            </label>
            <div className="flex flex-wrap gap-2">
              {reportTypes.map((type) => {
                const Icon = type.icon;
                return (
                  <motion.button
                    key={type.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setReportType(type.id)}
                    className={`inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      reportType === type.id
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
                    {type.name}
                  </motion.button>
                );
              })}
            </div>
          </div>

          {/* Period Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              الفترة الزمنية
            </label>
            <div className="flex gap-2">
              {periods.map((period) => (
                <motion.button
                  key={period.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedPeriod(period.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                    selectedPeriod === period.id
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {period.name}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
            >
              <EyeIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
              معاينة
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 rounded-lg transition-all duration-200"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-2 rtl:mr-0 rtl:ml-2" />
              تصدير PDF
            </motion.button>
          </div>
        </div>
      </div>

      {/* Dynamic Report Content */}
      {renderReportContent()}
    </div>
  );
};

export default ReportsTab;
